/**
 * Questions about chat state, asked the same way from both sides: an action
 * calls `threadFor(store.getState(), peer)`, a component calls
 * `useChatState((state) => threadFor(state, peer))`.
 *
 * They take state rather than the store precisely so the UI can subscribe to
 * them — a selector taking the store cannot be handed to `useChatState`.
 */
import type { Conversation, Message } from "../types"
import { messageHasId } from "../xmpp/stanzas/message"
import type { Jid } from "../xmpp/jid"
import type { ChatState } from "./store"

/**
 * Stable, so a peer with no thread yet does not re-render on every unrelated
 * store write — a fresh `[]` would be a new reference each time.
 */
const NO_MESSAGES: Message[] = []

const NO_TYPING: string[] = []

/** Who is typing in a peer's conversation, by handle. Stable when nobody is. */
export const typingIn = (state: ChatState, peer: Jid | undefined): string[] =>
  peer ? (state.typing[peer] ?? NO_TYPING) : NO_TYPING

/** One peer's thread, oldest-first. Undefined peer means nothing is open. */
export const threadFor = (
  state: ChatState,
  peer: Jid | undefined
): Message[] =>
  peer ? (state.messagesByPeer[peer] ?? NO_MESSAGES) : NO_MESSAGES

/** Whether a peer's history has loaded. Undefined peer means nothing is open. */
export const isHistoryLoaded = (state: ChatState, peer: Jid | undefined) =>
  peer ? state.historyLoaded[peer] === true : false

/** The conversation row for a peer, if the store knows it. */
export const conversationFor = (
  state: ChatState,
  peer: Jid
): Conversation | undefined => state.conversations.find((c) => c.peer === peer)

/**
 * One message in a peer's thread, found by either of its ids — a group message
 * is named by its origin-id here and by the room's stanza-id on the wire.
 */
export const messageIn = (
  state: ChatState,
  peer: Jid,
  messageId: string | undefined
): Message | undefined =>
  threadFor(state, peer).find((m) => messageHasId(m, messageId))

/** Whether this peer's conversation is the one on screen. */
export const isActive = (state: ChatState, peer: Jid): boolean =>
  peer === state.activePeer

/** True when any unmuted conversation has unread messages — drives the nav badge. */
export const hasUnread = (state: ChatState): boolean =>
  state.conversations.some((c) => c.unread && !c.muted)
